import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountTree,
  BarChart,
  Badge,
  Description,
  EventNote,
  Landscape,
  NoteAdd,
} from '@mui/icons-material';

import { IKiStatus } from '../Interfaces/IKi';

export interface IKiBeispiel {
  text: string;
  /** Platzhalter in GROSSBUCHSTABEN (FIRMA) — nach dem Einfügen markiert,
   *  damit man ihn gleich überschreibt. */
  platzhalter?: string;
  icon: React.ReactElement;
  /** Braucht eine angehängte Datei — der Klick öffnet die Auswahl. */
  datei?: boolean;
  /** Das Modell muss die Datei inhaltlich lesen (nicht jeder Anbieter kann das). */
  liestDatei?: boolean;
  /** Braucht Werkzeuge in DiNo. */
  siehtNach?: boolean;
  /** Auch zeigen, wenn der Anbieter es nicht kann — dann ausgegraut. */
  immerZeigen?: boolean;
}

/** Die Funktionen, die zeigen, was Karl kann (#1336). */
export const KI_BEISPIELE: IKiBeispiel[] = [
  {
    text: 'Was muss ich heute erledigen?',
    icon: <EventNote fontSize='small' />,
    siehtNach: true,
  },
  {
    text: 'Lies mir den beigefügten Grundbuchauszug aus',
    icon: <Landscape fontSize='small' />,
    datei: true,
    liestDatei: true,
    siehtNach: true,
    immerZeigen: true,
  },
  {
    text: 'Prüfe Gesellschafterliste und GwG-Kontrollstruktur der FIRMA',
    platzhalter: 'FIRMA',
    icon: <AccountTree fontSize='small' />,
    siehtNach: true,
  },
  {
    text: 'Welcher Mandant hat die meisten Vorgänge bei uns?',
    icon: <BarChart fontSize='small' />,
    siehtNach: true,
  },
  {
    text: 'Wandle die beigefügte PDF in ein Word-Dokument um',
    icon: <Description fontSize='small' />,
    datei: true,
    siehtNach: true,
  },
  {
    text: 'Ordne die Ausweise auf dem Foto den Beteiligten zu',
    icon: <Badge fontSize='small' />,
    datei: true,
    liestDatei: true,
    siehtNach: true,
  },
  {
    text: 'Lege einen neuen Vorgang an: Kaufvertrag VERKÄUFER an KÄUFER',
    platzhalter: 'VERKÄUFER an KÄUFER',
    icon: <NoteAdd fontSize='small' />,
    siehtNach: true,
  },
];

/** Kann der eingestellte Anbieter das Beispiel ausführen? */
export const beispielMoeglich = (
  b: IKiBeispiel,
  status: Pick<IKiStatus, 'SupportsAttachments' | 'CanLookUp'>
): boolean =>
  (!b.liestDatei || status.SupportsAttachments === true) &&
  (!b.siehtNach || status.CanLookUp !== false);

/**
 * Was angezeigt wird. Der Grundbuchauszug steht **immer** da (Vorgabe
 * 15.10.2026: die wichtigste Funktion soll man sehen) — kann der Anbieter
 * keine Dateien lesen, ausgegraut mit Begründung. Alles andere, was nicht
 * geht, entfällt.
 */
export const verfuegbareBeispiele = (
  status: Pick<IKiStatus, 'SupportsAttachments' | 'CanLookUp'>
): IKiBeispiel[] =>
  KI_BEISPIELE.filter(
    b => beispielMoeglich(b, status) || b.immerZeigen === true
  );

/**
 * Beispiele im leeren Chat — als Kacheln, zentriert. Ein Klick schreibt den
 * Text ins Eingabefeld; abgeschickt wird erst vom Benutzer, damit er „X"
 * vorher ersetzen kann.
 */
export const KiBeispiele: React.FC<{
  status: Pick<IKiStatus, 'SupportsAttachments' | 'CanLookUp'>;
  anbieter?: string | null;
  onWahl: (_beispiel: IKiBeispiel) => void;
}> = ({ status, anbieter, onWahl }) => {
  const beispiele = verfuegbareBeispiele(status);

  if (beispiele.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1,
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
      }}
    >
      {beispiele.map(b => {
        const moeglich = beispielMoeglich(b, status);
        return (
          <ButtonBase
            key={b.text}
            disabled={!moeglich}
            title={
              moeglich
                ? undefined
                : `Mit ${anbieter ?? 'diesem KI-Anbieter'} nicht möglich — er kann keine Dateien lesen.`
            }
            onClick={() => onWahl(b)}
            sx={{
              opacity: moeglich ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.25,
              p: 1.25,
              textAlign: 'left',
              borderRadius: 2.5,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'all 150ms ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme =>
                  `0 4px 14px ${alpha(theme.palette.primary.main, 0.15)}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              {b.icon}
            </Box>
            <Box>
              <Typography variant='body2' sx={{ lineHeight: 1.35 }}>
                {b.text}
              </Typography>
              {!moeglich && (
                <Typography variant='caption' color='text.secondary'>
                  mit {anbieter ?? 'diesem Anbieter'} nicht möglich
                </Typography>
              )}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
